import { API_BASE_URL } from './client';

function parseApiError(res, fallback) {
  return res.json().catch(() => ({})).then((err) => {
    const detail = err?.detail;
    if (typeof detail === 'string') throw new Error(detail);
    if (Array.isArray(detail)) {
      throw new Error(detail.map((d) => d.msg || JSON.stringify(d)).join('; '));
    }
    throw new Error(fallback);
  });
}

export async function sendAgentChatStream(keycloak, assistantId, payload) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/agent/chat/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, stream: true }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Agent chat failed (${res.status})`);
  }
  return res;
}

export async function listConnectors(keycloak) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to list connectors');
  return res.json();
}

export async function getConnector(keycloak, connectorId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to get connector');
  return res.json();
}

export async function createConnector(keycloak, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to create connector');
  return res.json();
}

export async function addConnectorTool(keycloak, connectorId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/tools/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to add endpoint');
  return res.json();
}

export async function updateConnectorTool(keycloak, connectorId, toolId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/tools/${toolId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to update tool');
  return res.json();
}

export async function updateConnector(keycloak, connectorId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to update connector');
  return res.json();
}

export async function deleteConnector(keycloak, connectorId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to delete connector');
  return res.json();
}

export async function setConnectorAssistantLink(keycloak, connectorId, assistantId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/assistants/${assistantId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to update assistant link');
  return res.json();
}

export async function removeConnectorAssistantLink(keycloak, connectorId, assistantId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/assistants/${assistantId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to remove assistant link');
  return res.json();
}

export async function previewConnectorToolRender(keycloak, connectorId, toolId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/${connectorId}/tools/${toolId}/preview-render/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to preview render');
  return res.json();
}

export async function linkAssistantConnector(keycloak, assistantId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/connectors/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to link connector');
  return res.json();
}

export async function listAssistantConnectors(keycloak, assistantId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/connectors/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to list assistant connectors');
  return res.json();
}

export async function seedTransportConnector(keycloak, assistantId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/connectors/seed-transport/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to seed transport connector');
  return res.json();
}

export async function seedTransportConnectorGlobal(keycloak) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connectors/seed-transport/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to seed transport connector');
  return res.json();
}

export async function listMcpServers(keycloak) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to list MCP servers');
  return res.json();
}

export async function getMcpServer(keycloak, serverId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to get MCP server');
  return res.json();
}

export async function createMcpServer(keycloak, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to create MCP server');
  return res.json();
}

export async function updateMcpServer(keycloak, serverId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to update MCP server');
  return res.json();
}

export async function deleteMcpServer(keycloak, serverId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to delete MCP server');
  return res.json();
}

export async function setMcpServerAssistantLink(keycloak, serverId, assistantId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/assistants/${assistantId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to update assistant link');
  return res.json();
}

export async function removeMcpServerAssistantLink(keycloak, serverId, assistantId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/assistants/${assistantId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to remove assistant link');
  return res.json();
}

export async function syncMcpTools(keycloak, serverId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/sync-tools/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to sync MCP tools');
  return res.json();
}

export async function connectMcpServer(keycloak, serverId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/connect/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to connect MCP server');
  return res.json();
}

export async function startMcpOAuth(keycloak, serverId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/mcp-servers/${serverId}/oauth/start/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to start MCP OAuth');
  return res.json();
}

export async function listNativeTools(keycloak) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/tool-sources/native`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to list native tools');
  return res.json();
}

export async function linkAssistantMcpServer(keycloak, assistantId, data) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/mcp-servers/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) return parseApiError(res, 'Failed to link MCP server');
  return res.json();
}

export async function listAssistantMcpServers(keycloak, assistantId) {
  if (!keycloak?.token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/mcp-servers/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!res.ok) return parseApiError(res, 'Failed to list assistant MCP servers');
  return res.json();
}
